import type { Variants } from 'motion/react'

/**
 * Shared motion tokens for the public (landing) site.
 * Every reveal, stagger and hover primitive reads from here so the site
 * animates with one voice. Change a value here, not in a page.
 */

/** Custom ease-out curve used across the landing site (Apple-style decelerate). */
export const REVEAL_EASE = [0.22, 1, 0.36, 1] as const

export const REVEAL_DURATION = 0.6
export const REVEAL_DISTANCE = 40
/**
 * Viewport margin for scroll reveals: the reveal starts once the element's leading edge is
 * 10% of the viewport height inside the bottom edge, so content is animating as it arrives
 * rather than popping in late.
 */
export const REVEAL_MARGIN = '0px 0px -10% 0px'
export const STAGGER_DELAY = 0.1
export const STAGGER_CHILD_DURATION = 0.5

/** Spring used for hover lifts and pressable feedback. */
export const HOVER_SPRING = { type: 'spring', stiffness: 300, damping: 20 } as const

export const fadeUpVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: STAGGER_CHILD_DURATION, ease: REVEAL_EASE },
  },
}

export const fadeInVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: STAGGER_CHILD_DURATION, ease: REVEAL_EASE },
  },
}

export const scaleInVariants: Variants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.4, ease: REVEAL_EASE },
  },
}

/** Parent variants: fades the container and staggers its children. */
export const staggerContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: STAGGER_DELAY, delayChildren: 0.05 },
  },
}

/**
 * Page-level enter/exit used by the public route shell. Opacity only: translating the whole
 * page promotes a viewport-sized layer, which flickers on mobile Safari.
 */
export const pageVariants: Variants = {
  initial: { opacity: 0 },
  enter: {
    opacity: 1,
    transition: { duration: 0.3, ease: REVEAL_EASE },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.15, ease: 'easeIn' },
  },
}

/* ---------------------------------------------------------------------------
   Display-heading reveals (BRAND-STYLE.md motion vocabulary)
   --------------------------------------------------------------------------- */

/** Duration for display-heading reveals. Marketing surface, so it may exceed the 300ms UI cap. */
export const HEADING_REVEAL_DURATION = 0.7

/** Stagger between lines of a multi-line display heading. */
export const LINE_STAGGER = 0.08

/** Parent for per-line heading reveals: staggers the line children. */
export const lineContainerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: LINE_STAGGER } },
}

/**
 * One line of a display heading: rises out of an overflow-hidden line box.
 * Full transform string so Motion keeps it hardware-accelerated under load.
 */
export const lineRevealVariants: Variants = {
  hidden: { transform: 'translateY(110%)' },
  visible: {
    transform: 'translateY(0%)',
    transition: { duration: HEADING_REVEAL_DURATION, ease: REVEAL_EASE },
  },
}

/** Block reveal for headings whose line breaks we don't control: wipes up via clip-path. */
export const clipRevealVariants: Variants = {
  hidden: { clipPath: 'inset(0 0 100% 0)', opacity: 0 },
  visible: {
    clipPath: 'inset(0 0 0% 0)',
    opacity: 1,
    transition: { duration: HEADING_REVEAL_DURATION, ease: REVEAL_EASE },
  },
}
